import { Card } from '@citizen-walk/shared-ui';
import { useMemo, useState } from 'react';
import { useAdminAuth } from '../auth';
import { filterOperationLogs, useOperationLogs } from './useOperationLogs';

/**
 * 操作ログ閲覧 page (admin 第 18 弾、REQ-4.3.26 MUST、functions 第 2/3/4/5/6/7 弾 audit chain の close)。
 *
 * 表示:
 *   - createdAt (新しい順)、actor (uid + email)、action (`<entity>.<verb>`)、target (type:id)、districtId、details (JSON 抜粋)
 *   - filter: action prefix (announcement / session / district / master / post / ecwInsight / ecwIdea / 空 = 全件) + districtId 任意
 *
 * 認可: master のみ閲覧可 (rules `allow read: if isMaster()`、admin 第 18 弾 で read 解禁)。
 *   district admin は本 page を開いてもエラーで弾かれる仕様 (UI でも guard、無権限案内表示)。
 *
 * 旧 v1 に該当 page 無し → v2 新規 (REQ-4.3.26 MUST 対応、長期保存ログの自前 viewer)。
 */

// 各 prefix の operationLogs 書込元 (実装状況):
//   - announcement.* : functions 第 2 弾 onAnnouncementWrite (full)
//   - session.*      : functions 第 3 弾 onSessionWrite (full)
//   - district.*     : functions 第 4 弾 onDistrictWrite (full、created/archived/unarchived/token-rotated/updated/deleted)
//                       + admin/generatePmtiles (`district.generate-pmtiles`)
//   - master.*       : functions 第 5 弾 onMasterWrite で実機能化済 (M1〜M9、created/updated/deleted、
//                       action='master.{masterName}-{verb}' 形式。archive/restore も updated に丸める、
//                       詳細 changeType は history collection 側で追跡)
//   - post.*         : functions 第 7 弾 onPostWrite で実機能化 (created/visibility-changed/updated/deleted、
//                       admin 第 11 弾 posts-moderation の audit chain close)
//                       + admin/exportPosts (`post.export-csv` / `post.export-geojson` 別書込)
//                       注: aiAnalysis 更新は trigger 騒音回避のため noop 化
//   - ecwInsight.*   : functions 第 6 弾 onEcwInsightWrite で実機能化 (created/hidden/unhidden/deleted、
//                       admin 第 19 弾 ECW Insight/Idea モデレーション後追い)
//   - ecwIdea.*      : functions 第 6 弾 onEcwIdeaWrite で実機能化 (同 4 種 event)
const ACTION_PRESETS = [
  { value: '', label: '全件' },
  { value: 'announcement.', label: 'announcement.*' },
  { value: 'session.', label: 'session.*' },
  { value: 'district.', label: 'district.*' },
  { value: 'master.', label: 'master.*' },
  { value: 'post.', label: 'post.*' },
  { value: 'ecwInsight.', label: 'ecwInsight.*' },
  { value: 'ecwIdea.', label: 'ecwIdea.*' },
];

function fmt(date: Date | null): string {
  if (!date) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function OperationLogsPage() {
  const { state: authState } = useAdminAuth();
  const logsState = useOperationLogs(200);
  const [actionPrefix, setActionPrefix] = useState('');
  const [districtIdFilter, setDistrictIdFilter] = useState('');

  const filtered = useMemo(() => {
    if (logsState.status !== 'ready') return [];
    return filterOperationLogs(logsState.items, {
      actionPrefix,
      districtId: districtIdFilter.trim() === '' ? null : districtIdFilter.trim(),
    });
  }, [logsState, actionPrefix, districtIdFilter]);

  // master のみ閲覧可 (rules でも deny されるが UI でも明示)。
  if (authState.role !== 'master') {
    return (
      <Card padding="lg">
        <h2 className="text-lg font-bold text-slate-900 mb-2">権限がありません</h2>
        <p className="text-sm text-slate-700">
          操作ログ閲覧は <strong>master</strong> ロール限定です。
        </p>
      </Card>
    );
  }

  return (
    <>
      <header className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900">操作ログ閲覧</h1>
        <p className="text-sm text-slate-600 mt-1">
          REQ-4.3.26 — Functions Admin SDK が `/operationLogs` に書込んだ監査ログを最新 200 件表示。
          functions 第 2 弾 (announcement) + 第 3 弾 (session) + 第 4 弾 (district) + 第 5 弾
          (master M1〜M9) + 第 6 弾 (ecwInsight / ecwIdea) + 第 7 弾 (post) で稼働。
        </p>
      </header>

      <Card padding="md" className="mb-4">
        <div className="flex items-center gap-3 flex-wrap text-sm">
          <label className="flex items-center gap-2">
            <span className="text-slate-600">action:</span>
            <select
              value={actionPrefix}
              onChange={(e) => setActionPrefix(e.target.value)}
              className="border border-slate-300 rounded px-2 py-1 min-h-touch"
            >
              {ACTION_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-slate-600">districtId:</span>
            <input
              type="text"
              value={districtIdFilter}
              onChange={(e) => setDistrictIdFilter(e.target.value)}
              placeholder="(空 = 全地区 + 地区横断)"
              className="border border-slate-300 rounded px-2 py-1 min-h-touch w-64"
            />
          </label>
          <span className="text-xs text-slate-500 ml-auto">
            {logsState.status === 'ready' && (
              <>
                表示: <strong>{filtered.length}</strong> / 全{logsState.items.length} 件
              </>
            )}
          </span>
        </div>
      </Card>

      {logsState.status === 'loading' && <p className="text-sm text-slate-500">読込中…</p>}
      {logsState.status === 'error' && (
        <Card padding="md">
          <p className="text-sm text-danger-600">読込エラー: {logsState.error.message}</p>
          <p className="text-xs text-slate-500 mt-1">
            rules で master のみ read 可。権限の有無を確認してください。
          </p>
        </Card>
      )}
      {logsState.status === 'ready' && filtered.length === 0 && (
        <Card padding="md">
          <p className="text-sm text-slate-500">条件に一致するログはありません。</p>
        </Card>
      )}
      {logsState.status === 'ready' && filtered.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border-collapse">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="border px-2 py-1 whitespace-nowrap">時刻</th>
                <th className="border px-2 py-1 whitespace-nowrap">action</th>
                <th className="border px-2 py-1 whitespace-nowrap">target</th>
                <th className="border px-2 py-1 whitespace-nowrap">districtId</th>
                <th className="border px-2 py-1 whitespace-nowrap">actor uid</th>
                <th className="border px-2 py-1 whitespace-nowrap">email</th>
                <th className="border px-2 py-1">details</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((log) => (
                <tr key={log.id} className="hover:bg-slate-50">
                  <td className="border px-2 py-1 whitespace-nowrap text-slate-700">
                    {fmt(log.createdAt)}
                  </td>
                  <td className="border px-2 py-1 whitespace-nowrap font-mono">{log.action}</td>
                  <td className="border px-2 py-1 whitespace-nowrap font-mono text-slate-600">
                    {log.targetType}:{log.targetId}
                  </td>
                  <td className="border px-2 py-1 whitespace-nowrap font-mono text-slate-600">
                    {log.districtId ?? <span className="text-slate-400">(横断)</span>}
                  </td>
                  <td className="border px-2 py-1 whitespace-nowrap font-mono text-slate-600">
                    {log.actorUid}
                  </td>
                  <td className="border px-2 py-1 whitespace-nowrap text-slate-600">
                    {log.actorEmail ?? <span className="text-slate-400">—</span>}
                  </td>
                  <td className="border px-2 py-1 text-slate-700">
                    <code className="text-[10px]">{JSON.stringify(log.details)}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
